import { Alert, AlertTitle, Chip, Fade, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow, Tooltip } from "@mui/material";
import { Card } from "randmarcomps";
import React, { useState, useCallback } from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import withManufacturer from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { Receipt } from "../../stores/document/Document";
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt from "../util/DateTimeUtil";
import { useGetV4WarehousesQuery } from "randmar-api-client";

interface IProps {
  receipts?: Receipt[];
  defaultRowsPerPage?: number;
  applicationId: string;
}

const ReceiptsGrid: React.FC<IProps> = ({ receipts, defaultRowsPerPage = 20, applicationId }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const { data: warehouses } = useGetV4WarehousesQuery();

  const getWarehouseNameFromCode = useCallback((code: string | undefined | null): string => {
    if (!code || !warehouses) {
      return "B/O";
    }
    const warehouse = warehouses.find(w => w.WarehouseId === code);
    return warehouse?.Name || "B/O";
  }, [warehouses]);

  const handleChangePage = useCallback((_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  }, []);
  if (!receipts || receipts.length === 0) {

    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle color="#3b6cca">What are Receptions?</AlertTitle>
        Receptions are documented details of purchase order receptions in our warehouses. Once we receive your inventory, these documents will appear here.
      </Alert>
    );
  }

  return (
    <Card className="w-full bg-white">
      <div className="w-full overflow-auto rounded-[20px]">
        <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: "#f9f5ff" }}>
            <TableCell align="center" sx={{ borderColor: "#582c83" }} />
            <TableCell align="center" sx={{ borderColor: "#582c83" }}>Reception Number</TableCell>
            <TableCell align="center" sx={{ borderColor: "#582c83" }}>Purchase Order Number</TableCell>
            <TableCell align="center" sx={{ borderColor: "#582c83" }}>Location</TableCell>
            <TableCell align="center" sx={{ borderColor: "#582c83" }}>Reception Date</TableCell>
            <TableCell align="center" sx={{ borderColor: "#582c83" }}>Sub Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {receipts
            .slice()
            .sort((a, b) => (b.receiptNumber || "").localeCompare(a.receiptNumber || ""))
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((row, index) => (
              <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }} key={"Receipt" + index}>
                <Tooltip
                  arrow
                  title={
                    <>
                      <div>Comments: {row.comments}</div>
                      {row.invoiceNumber && <div>Invoice #: {row.invoiceNumber}</div>}
                    </>
                  }
                  TransitionComponent={Fade}
                >
                  <TableCell>
                    <Chip variant={row.invoiced ? "outlined" : "filled"} label={row.invoiced ? "Invoiced" : "Uninvoiced"} color="primary" size="small" />
                  </TableCell>
                </Tooltip>
                <TableCell align="center"><Link to={Path.document(applicationId, row.receiptNumber)}>{row.receiptNumber}</Link></TableCell>
                <TableCell align="center">{row.purchaseOrderNumber}</TableCell>
                <TableCell align="center">{getWarehouseNameFromCode(row.location)}</TableCell>
                <TableCell align="center">{FormatDateFromInt(row.receiptDate)}</TableCell>
                <TableCell align="center"><FormattedNumber {...CurrencyUtils.format} value={row.subTotal || 0} /></TableCell>
              </TableRow>
            ))}
        </TableBody>
        {receipts.length > rowsPerPage ?
          <TableFooter>
            <TableRow>
              <TablePagination
                count={receipts.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableRow>
          </TableFooter> : undefined}
        </Table>
      </div>
    </Card>
  );
};

export default withManufacturer(ReceiptsGrid);
